import React, { useContext, useState } from 'react';
import './styles.scss';
import { addOverlay, OverlayEnum, SspProviderContext } from '../../did-utility';
import SSPContentVO from 'sspcontentext/src/model/SSPContentVO';
import { UpperTableElement } from './upperTableElement';
import i18next from 'i18next';
import { checkHtml } from '../../did-utility/src/Functions/checkHTML';
import { TeaserImage } from '../../did-utility/src/Components/TeaserImage';
import Disclaimers from '../../did-utility/src/Components/Disclaimers';
import { getSetup } from '../../did-utility/src/Functions/getSetup';
import { showInfoIcon } from '../../did-utility/src/Functions/showInfoIcon';
import order, { tileConfig, usedCarTileConfig } from './TileConfig';

export default function Overview(): JSX.Element {
	const store = useContext(SspProviderContext);
	const contentModel = store.state.content
		? store.state.content.asModel
		: new SSPContentVO('').asModel;
	const vehicleDataModel = contentModel.ref('vehicle_data').asModel;
	const engine = contentModel.ref('engine').asModel;
	const highlightEquip = contentModel
		.reflist('highlight_equip')
		.filter(item => item.asModel.bool('marketable', true));
	const usedCar = store.state.content?.contentType === 'UsedPricesheet';
	const moreData = (
		<p
			className='small more-data'
			onClick={() => addOverlay(store, OverlayEnum.Consumption)}
		>
			{i18next.t('LinkAdditionalConsumptionData')}
			<svg className='icon-12'>
				<use xlinkHref='#chevron-xsmall' />
			</svg>
		</p>
	);

	const engineDisclaimers = engine.arr('disclaimers') as Array<{
		Key: string;
		Value: string;
	}>;
	const disclaimerTags: Array<string> = [];
	['consumption.fuel-type'].forEach(tag => {
		if (engineDisclaimers.findIndex(dis => dis.Key === tag) !== -1)
			disclaimerTags.push(tag);
	});
	const kvps = contentModel.target.market_specific_list.find(
		(item: { Key: string; Value: string; }) => item.Key === 'KVPS'
	).Value;
	localStorage.setItem('kvps', kvps.match(/\d+/)[0] ?? null);

	const [showMoreOpen, setShowMoreOpen] = useState<boolean>(false);
	const setup = getSetup();
	const params = new URLSearchParams(window.location.search);
	const apk = params.get('apk') === 'true';
	const allHaveAsset = highlightEquip?.every(item =>
		item.asModel.has('assets')
	);
	const newCarTiles = tileConfig.default || {};
	const usedCarTiles = usedCarTileConfig.default || {};

	const reorderAndRenderTiles = (
		tiles: { id: string; class?: string; condition?: any; getProps: any }[],
		order?: string[]
	) => {
		let finalTiles = tiles;
		if (order) {
			const validTiles = tiles.filter(tile =>
				tile.condition(engine, contentModel, setup)
			);

			const usedTiles = new Set();

			const orderedTiles = order.map((id, index) => {
				const matchingTile = validTiles.find(
					tile => tile.id.includes(id) && !usedTiles.has(tile)
				);
				if (matchingTile) {
					usedTiles.add(matchingTile);
					return matchingTile;
				}
				return { id: `PLACEHOLDER_${index}` } as any; // Temporary placeholder
			});

			const remainingTiles = validTiles.filter(
				tile => !usedTiles.has(tile)
			);

			// Replace placeholders with remaining tiles
			finalTiles = orderedTiles.map(tile => {
				if (!tile.id.startsWith('PLACEHOLDER_')) return tile;
				const replacementTile = remainingTiles.shift();
				return replacementTile ?? null;
			});
		}

		return finalTiles.map(tile => {
			if (tile.id.startsWith('PLACEHOLDER_')) return;
			const props = tile.getProps(
				engine,
				i18next,
				contentModel,
				moreData,
				vehicleDataModel
			);
			return (
				<UpperTableElement
					key={tile.id}
					className={
						(tile.class ? tile.class : '') +
						' ' +
						(engine?.str('engine_type') === 'HybridPetrol'
							? 'hybrid'
							: '')
					}
					engine={engine}
					{...props}
				/>
			);
		});
	};

	const overviewTiles = reorderAndRenderTiles(newCarTiles, order);
	const overviewUsedCarTiles = usedCar && reorderAndRenderTiles(usedCarTiles);

	return (
		<>
			<div className='upper-table overview'>
				{overviewTiles}
				{usedCar && (
					<div className='plus-btn-container'>
						<button
							className='showMore'
							onClick={() => setShowMoreOpen(!showMoreOpen)}
						>
							<svg className='icon-48'>
								<use
									xlinkHref={
										showMoreOpen
											? '#cancel-circle'
											: '#add-circle'
									}
								/>
							</svg>
							<p>
								{i18next.t(
									showMoreOpen
										? 'ShowLess'
										: 'ShowMoreInformation'
								)}
							</p>
						</button>
					</div>
				)}
				{usedCar && showMoreOpen && <>{overviewUsedCarTiles}</>}
			</div>

			{allHaveAsset ? (
				highlightEquip.map((equipItem, index) => (
					<div
						className='teaser-box'
						key={`teaser-box-${index}`}
						onClick={() => addOverlay(store, equipItem)}
					>
						<TeaserImage
							equipItem={equipItem}
							iconVideo='#play-circle'
						/>
						<div className='teaser-box-right'>
							<p className='small category'>
								{(equipItem.asModel.target.equipcategory &&
									equipItem.asModel.target.equipcategory[
										i18next.language
									]) ||
									equipItem.asModel.str('equipcategory')}
							</p>
							<div className={`truncate ${apk ? 'apk' : ''}`}>
								<h3>
									{(equipItem.asModel.target.layertitle &&
										equipItem.asModel.target.layertitle[
											i18next.language
										]) ||
										equipItem.asModel.str('layertitle')}
								</h3>
								<p
									className='long'
									dangerouslySetInnerHTML={checkHtml(
										(equipItem.asModel.target[
											'teasertext'
										] &&
											equipItem.asModel.target[
												'teasertext'
											][i18next.language]) ||
											equipItem.asModel.str('teasertext'),
										i18next.t('ClickMe')
									)}
								/>
							</div>

							<button>
								{i18next.t('LearnMore')}
								<svg className='icon-24'>
									<use xlinkHref='#chevron-right' />
								</svg>
							</button>
						</div>
					</div>
				))
			) : (
				<div className='highlight-table'>
					<div className='table-element headline'>
						<h4 className='bold'>
							{i18next.t('EquipmentHighlights')}
						</h4>
					</div>
					{highlightEquip.map((equipItem, index) => {
						const item = equipItem.asModel;
						const showIcon = showInfoIcon(equipItem);
						return (
							<div
								key={`table-element-${index}`}
								className={`table-element${
									showIcon ? ' with-end' : ''
								}`}
								onClick={() => {
									if (showIcon) {
										addOverlay(store, equipItem);
									}
								}}
							>
								<p className='short'>
									{(item.target.layertitle &&
										item.target.layertitle[
											i18next.language
										]) ||
										item.str('layertitle')}
								</p>
								{showIcon && (
									<div className='icon-24 info-icon'>
										<svg>
											<use xlinkHref='#system-info' />
										</svg>
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}
			<div className='disclaimers pre-wrap'>
				{disclaimerTags.length > 0 && (
					<Disclaimers
						tagsUsed={disclaimerTags}
						complete={engineDisclaimers}
					/>
				)}
			</div>
		</>
	);
}
